import type { FeedbackResult } from '@trello/ai-labs/feedback-collector';
import { Analytics } from '@trello/atlassian-analytics';
import { smartScheduledCardsSharedState } from '@trello/business-logic/planner';

import type { ProposedEvent } from './useProposedEvents';

export const SMART_SCHEDULE_AI_ANALYTICS_CONFIG = {
  aiFeatureName: 'aiSmartSchedule',
  isAIFeature: 1,
  userGeneratedAI: 1,
  proactiveGeneratedAI: 0,
};

export const SMART_SCHEDULE_PROACTIVE_AI_ANALYTICS_CONFIG = {
  aiFeatureName: 'aiSmartSchedule',
  isAIFeature: 1,
  userGeneratedAI: 0,
  proactiveGeneratedAI: 1,
};

export interface SmartScheduleFeedbackJourney {
  isProactive: boolean;
  singleInstrumentationID: string | undefined;
}

/**
 * Resolves the AI journey a rated proposal belongs to. An automatic proposal is
 * its own scheduling request; a manually requested one shares the run's trace.
 * Mirrors the selection `ProposedEventPreview` uses for `aiResult viewed`, so
 * feedback lands on the same journey as the view it is rating.
 */
export const getSmartScheduleFeedbackJourney = (
  ratedProposedEvent: ProposedEvent | undefined,
): SmartScheduleFeedbackJourney => {
  const isProactive = ratedProposedEvent?.source === 'PROACTIVE';

  return {
    isProactive,
    singleInstrumentationID: isProactive
      ? (ratedProposedEvent?.schedulingRequestId ?? undefined)
      : smartScheduledCardsSharedState.value.traceId,
  };
};

export const trackSmartScheduleFeedbackSubmitted = (
  aiFeedbackResult: FeedbackResult,
  { isProactive, singleInstrumentationID }: SmartScheduleFeedbackJourney,
): void => {
  // No journey id means the feedback can't be joined to its `aiResult viewed`.
  if (!singleInstrumentationID) {
    return;
  }

  Analytics.sendTrackEvent({
    action: 'submitted',
    actionSubject: 'aiFeedback',
    source: 'smartScheduler',
    attributes: {
      ...(isProactive
        ? SMART_SCHEDULE_PROACTIVE_AI_ANALYTICS_CONFIG
        : SMART_SCHEDULE_AI_ANALYTICS_CONFIG),
      singleInstrumentationID,
      aiFeedbackResult,
    },
  });
};
